import { ICartItems, IOrderItem, IInsuranceOrderItem, IPrice, IPeople, ITicketOrderItem } from '../types';
import { calculateInsuranceFee } from '../../utils/priceHelper';
import { PREV_CART_VERSION, CURRENT_CART_VERSION, CART_EXPIRES } from '../constants';
import * as localStorage from '../../utils/localStorage';
import t from '../locales';
import { ITEM_ADDED_TO_CART, ITEM_REMOVED_FROM_CART, ITEM_UPDATED_IN_CART } from '../events';

export function formatCartItems(cartItems: any = {}): ICartItems {
  const { orderItems, insuranceOrderItems, yadoOrderItems, jrwTicketOrderItems, id, migrated, twpTicketOrderItems, jepTicketOrderItems} = cartItems;
  return {
    orderItems: orderItems || [],
    insuranceOrderItems: insuranceOrderItems || [],
    yadoOrderItems: yadoOrderItems || [],
    jrwTicketOrderItems: jrwTicketOrderItems || [],
    id,
    migrated: migrated || false,
    twpTicketOrderItems: twpTicketOrderItems || [],
    jepTicketOrderItems: jepTicketOrderItems || []
  };
}

export function countCartItems(cartItems: ICartItems): number {
  const { orderItems, insuranceOrderItems, yadoOrderItems, jrwTicketOrderItems, twpTicketOrderItems, jepTicketOrderItems } = formatCartItems(cartItems);
  return orderItems.length + insuranceOrderItems.length + yadoOrderItems.length + jrwTicketOrderItems.length + twpTicketOrderItems.length + jepTicketOrderItems.length;
}

export function retrieveCartItems(storageVersion: string): ICartItems {
  if (storageVersion !== PREV_CART_VERSION && storageVersion !== CURRENT_CART_VERSION) {
    throw new Error('storageVersion is invalid');
  }

  const cartItems = localStorage.get(storageVersion) || '{}';
  return formatCartItems(cartItems);
}

export function removeCartItems(storageVersion: string): void {
  localStorage.remove(storageVersion);
}

export function storeCartItems(cartItems: ICartItems): void {
  const prevCartItems: ICartItems = retrieveCartItems(CURRENT_CART_VERSION);
  const currentCartItems = formatCartItems(cartItems);
  const trackingFlag = localStorage.get('userPutItemsToCartWithoutLoggingIn') ? true : false;

  console.log(JSON.stringify(currentCartItems))

  // Store cart item in DB
  fetch('/snow/api/v1/me/shopping_cart.json', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version: CURRENT_CART_VERSION, cart_items: currentCartItems, tracking_flag: trackingFlag }),
  })
  .then(res => {
    // 未サインイン状態でDBにカート情報を更新し、401: Authentication Errorの場合
    if (res.status === 401) {
      return false;

    // 想定しないエラー
    } else if (res.status !== 200) {
      throw new Error(res.status.toString());
    }
    return res.json();
  })
  .then(json => {
    let storingCartItems:ICartItems;
    if (json) {
      storingCartItems = { ...currentCartItems, id: json.id };
    } else {
      storingCartItems = currentCartItems;
      localStorage.set('userPutItemsToCartWithoutLoggingIn', 'true', CART_EXPIRES);
    }
    localStorage.set(CURRENT_CART_VERSION, storingCartItems, CART_EXPIRES);
    _dispatch(prevCartItems, storingCartItems);
  })
  .catch((error) => {
    alert(t.cannotAddToShoppingCart());
    console.log(error);
  });

  function _dispatch(prevCartItems: ICartItems, currentCartItems: ICartItems): void {
    const prevCount = countCartItems(prevCartItems);
    const currentCount = countCartItems(currentCartItems);
    const eventData = {
      detail: {
        cartItems: currentCartItems,
      },
    };
    if (currentCount > prevCount) {
      const cartEvent = new CustomEvent(ITEM_ADDED_TO_CART, eventData);
      document.dispatchEvent(cartEvent);
    } else if (currentCount < prevCount) {
      const cartEvent = new CustomEvent(ITEM_REMOVED_FROM_CART, eventData);
      document.dispatchEvent(cartEvent);
    } else {
      const cartEvent = new CustomEvent(ITEM_UPDATED_IN_CART, eventData);
      document.dispatchEvent(cartEvent);
    }
  }
}

export function migrateCartItems(): void {
  // 将来新しいバージョンのカートが出てきたら新旧をマージする処理をここに記載
}

export function calculateItemPrice(price: IPrice, people: IPeople): { price: number, fullPrice: number } {
  const { adultPrice, childPrice, groupPrice, studentPrice, seniorPrice, adultFullPrice, childFullPrice, groupFullPrice, studentFullPrice, seniorFullPrice, insuranceApplicable, maxParticipantsAtOnce, forGroup } = price;
  const { adultNum, childNum, studentNum, seniorNum } = people;
  // nullは0として計算されるのでsnow_items.limited_toで制御することを忘れずに
  const totalPrice = ((adultPrice || 0) * adultNum) + ((childPrice || 0) * childNum) + (groupPrice || 0) + ((studentPrice || 0) * studentNum) + ((seniorPrice || 0) * seniorNum);
  const totalFullPrice = ((adultFullPrice || 0) * adultNum) + ((childFullPrice || 0) * childNum) + (groupFullPrice || 0) + ((studentFullPrice || 0) * studentNum) + ((seniorFullPrice || 0) * seniorNum);

  // 保険料を計算して追加
  const totalPeopleCount = adultNum + childNum + studentNum + seniorNum;
  const insuranceFee = calculateInsuranceFee(
    insuranceApplicable,
    forGroup,
    maxParticipantsAtOnce,
    totalPeopleCount
  );

  return {
    price: totalPrice + insuranceFee,
    fullPrice: totalFullPrice + insuranceFee
  };
}

/**
 * JRW用↓↓↓
 */
export const calculateJRWItemPrice = (adultSellingPrice: number, numPassengers: number) => {
  return adultSellingPrice * numPassengers
}

export const calculateTotalPriceWithJRW = (cartItems: ICartItems) => {
  const result = { price: 0, fullPrice: 0 };
  const { orderItems, insuranceOrderItems, yadoOrderItems, jrwTicketOrderItems } = cartItems;
  [orderItems, insuranceOrderItems].forEach((items) => {
    if (items != null) {
      items.forEach((item: IOrderItem|IInsuranceOrderItem) => {
        const itemPrice = calculateItemPrice(item.price, item.people);
        result.price = result.price + itemPrice.price;
        result.fullPrice = result.fullPrice + itemPrice.fullPrice;
      });
    }
  });
  jrwTicketOrderItems.forEach((item) => {
    const itemPrice = calculateJRWItemPrice(item.plan.adultSellingPrice, item.numPassengers);
    result.price += itemPrice
    result.fullPrice += itemPrice
  });
  yadoOrderItems.forEach((item) => {
    result.price = result.price + item.totalAmountIncludingTax;
    result.fullPrice = result.fullPrice + item.totalAmountIncludingTax;
  });
  return result;
}
/**
 * JRW用↑↑↑
 */

/**
 * 汎用的なメソッド
 * TODO: JRW〜メソッドをやめて、下記のを使う
 */
export const returnTicketTotalAmount = (unitPrice, numUnits) => {
  if (typeof unitPrice == 'string') {
    unitPrice = parseInt(unitPrice)
  }
  if (typeof numUnits == 'string') {
    numUnits = parseInt(numUnits)
  }
  return unitPrice * numUnits
}

export function returnTotalAmount(cartItems: ICartItems) {
  const result = { price: 0, fullPrice: 0 };
  const { orderItems, insuranceOrderItems, yadoOrderItems, jrwTicketOrderItems, twpTicketOrderItems, jepTicketOrderItems } = cartItems;
  [orderItems, insuranceOrderItems].forEach((items) => {
    if (items != null) {
      items.forEach((item: IOrderItem|IInsuranceOrderItem) => {
        const itemPrice = calculateItemPrice(item.price, item.people);
        result.price = result.price + itemPrice.price;
        result.fullPrice = result.fullPrice + itemPrice.fullPrice;
      });
    }
  });
  jrwTicketOrderItems.forEach((item) => {
    const itemPrice = calculateJRWItemPrice(item.plan.adultSellingPrice, item.numPassengers);
    result.price += itemPrice
    result.fullPrice += itemPrice
  });
  yadoOrderItems.forEach((item) => {
    result.price = result.price + item.totalAmountIncludingTax;
    result.fullPrice = result.fullPrice + item.totalAmountIncludingTax;
  });
  twpTicketOrderItems.forEach((item) => {
    const plan = item.ticket.plans.find(p => p.planId === item.planId)
    const itemPrice = returnTicketTotalAmount(plan.adultSellingPrice, item.adultQuantity)
    result.price += itemPrice
    result.fullPrice += itemPrice
  });
  jepTicketOrderItems.forEach((item) => {
    const plan = item.ticket.plans.find(p => p.planId === item.planId)
    const itemPrice = returnTicketTotalAmount(plan.adultSellingPrice, item.adults.length) + returnTicketTotalAmount(plan.childSellingPrice, item.children.length)
    result.price += itemPrice
    result.fullPrice += itemPrice
  });
  return result;
}
